import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState
} from 'react';

export interface AuthUser {
  id?: number;
  firstName?: string;
  lastName?: string;
  email?: string;
  role?: string;
  api_token?: string;
  permissions?: string[];
  [key: string]: any;
}

interface AuthContextValue {
  user: AuthUser | null;
  permissions: Set<string>;
  hasPermission: (permissionKey: string) => boolean;
  login: (userData: AuthUser) => void;
  logout: () => void;
  refreshPermissions: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

const readUserFromStorage = (): AuthUser | null => {
  try {
    return JSON.parse(localStorage.getItem('user') || 'null');
  } catch {
    return null;
  }
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children
}) => {
  const [user, setUser] = useState<AuthUser | null>(readUserFromStorage);

  const login = useCallback((userData: AuthUser) => {
    localStorage.setItem('user', JSON.stringify(userData));
    setUser(userData);
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem('user');
    setUser(null);
  }, []);

  // Picks up a permission change an admin makes mid-session without forcing
  // a logout. The backend already enforces the true, current permission set
  // on every request regardless of this — this only affects how fresh the
  // UI is.
  const refreshPermissions = useCallback(async () => {
    const current = readUserFromStorage();
    if (!current) return;
    try {
      const { getMe } = await import('../services/authService');
      const me = await getMe();
      if (me) {
        const updated = { ...current, role: me.role, permissions: me.permissions };
        localStorage.setItem('user', JSON.stringify(updated));
        setUser(updated);
      }
    } catch {
      // Ignore — keep the last-known permissions cached client-side.
    }
  }, []);

  const permissions = useMemo(
    () => new Set<string>(user?.permissions || []),
    [user]
  );

  const hasPermission = useCallback(
    (permissionKey: string) => permissions.has(permissionKey),
    [permissions]
  );

  const value = useMemo<AuthContextValue>(
    () => ({ user, permissions, hasPermission, login, logout, refreshPermissions }),
    [user, permissions, hasPermission, login, logout, refreshPermissions]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextValue => {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return ctx;
};
